// eslint-disable-next-line @altermaind/you-shall-not-pass
import {
  ComponentProps,
  FC,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Dialog } from '@/_shared/components/Dialog';
import { Form, SelectItem } from '@/_shared/components/Form';
import { EMAIL_REGEX } from '@/_shared/constants';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useNotification } from '@/_shared/hooks';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import {
  getDepartmentsCall,
  getDepartmentTeamsCall,
  ResponseGetDepartmentTeams,
} from '@/departments/state/api';
import { Department } from '@/departments/state/types';
import {
  getEmployeesCall,
  getEmployeesStatsCall,
  postEmailAlreadyExistCall,
  postEmployeeCall,
} from '@/people/state';

type Team = NonNullable<ResponseGetDepartmentTeams['data']>[number];

export const AddEmployeeFormDialog: FC = () => {
  const t = useTranslations('people.addEmployee.formDialog');
  const tShared = useTranslations();
  const tEditProfile = useTranslations('profileCard.editProfile');
  const { notify } = useNotification();
  const { genericResponsiveValue } = useResponsiveStyle();
  const gapXS = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`);

  const { isOpen, closeDialog } = useDialog('addEmployeeManual');
  const { execute: executeGetEmployees } = getEmployeesCall.hook();
  const { execute: executeGetEmployeesStats } = getEmployeesStatsCall.hook();

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [teamsList, setTeamsList] = useState<Team[]>([]);

  const submitRef = useRef<HTMLElement>(null);

  const {
    execute: executeGetDepartments,
    data: departments,
    loading: loadingGetDepartments,
  } = getDepartmentsCall.hook();
  const { execute: executeGetDepartmentTeams, loading: loadingGetDepartmentTeams } =
    getDepartmentTeamsCall.hook();

  const { execute: executePostEmployee, loading: loadingPostEmployee } = postEmployeeCall.hook();
  const {
    execute: executePostEmailAlreadyExist,
    loading: loadingPostEmailAlreadyExist,
    emailAlreadyExist,
  } = postEmailAlreadyExistCall.hook();

  const [emailChecked, setEmailChecked] = useState('');
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const rowDirection = genericResponsiveValue<ComponentProps<typeof Box>['flexDirection']>({
    breakPoints: { desktop: 'row' },
    defaultValue: 'column',
  });

  const {
    handleSubmit,
    formState: { isValid, touchedFields },
    control,
    watch,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      fiscalCode: '',
      role: '',
      department: { value: '', label: '' } as SelectItem | undefined,
      area: { value: '', label: '' } as SelectItem | undefined,
      emailManager: '',
      jobLevel: '',
      birthDate: '',
    },
  });

  const fieldValues = watch();
  const deptValue = watch('department');
  const isDeptSelected = useMemo(
    () => deptValue && typeof deptValue === 'object' && 'value' in deptValue && !!deptValue.value,
    [deptValue],
  );
  const isEmailAlreadyRegistered = useMemo(
    () => emailAlreadyExist === true && emailChecked !== '' && emailChecked === fieldValues.email,
    [emailAlreadyExist, emailChecked, fieldValues.email],
  );

  const canSubmit = useMemo(
    () => isValid && EMAIL_REGEX.test(fieldValues.email) && !isEmailAlreadyRegistered,
    [isValid, fieldValues.email, isEmailAlreadyRegistered],
  );

  const deptItems: SelectItem[] = useMemo(
    () =>
      ((departments as Department[] | undefined) || [])
        .filter(d => d.name?.toLowerCase() !== 'unknown')
        .map(d => ({
          value: d.id || '',
          label: d.name || '',
        })),
    [departments],
  );

  const teamItems: SelectItem[] = useMemo(
    () =>
      teamsList
        .filter(team => team.name?.toLowerCase() !== 'unknown')
        .map(team => ({ value: team.id || '', label: team.name || '' })),
    [teamsList],
  );

  const isTeamEnabled = useMemo(
    () => isDeptSelected && teamItems.length > 0,
    [isDeptSelected, teamItems?.length],
  );

  const fetchTeams = useCallback(
    (deptId: string) => {
      executeGetDepartmentTeams({
        departmentId: deptId,
        successSaga: ({ response }) => {
          setTeamsList(response?.data || []);
        },
      });
    },
    [executeGetDepartmentTeams],
  );

  const handleDeptSelect = useCallback(
    (item: SelectItem) => {
      const dept = (departments as Department[] | undefined)?.find(d => d.id === item.value);
      setValue('emailManager', dept?.manager?.email || '');
      setValue('area', undefined);
      setTeamsList([]);
      fetchTeams(item.value);
    },
    [departments, fetchTeams, setValue],
  );

  const handleTeamSelect = useCallback(
    (item: SelectItem) => {
      const selectedTeam = teamsList.find(team => team.id === item.value);
      setValue('emailManager', selectedTeam?.manager?.email || '');
    },
    [teamsList, setValue],
  );

  const handleEmailBlur = useCallback(
    (event: FocusEvent<HTMLDivElement, Element>) => {
      if (!EMAIL_REGEX.test(fieldValues.email)) return;
      setEmailChecked(fieldValues.email);
      const hasBlurredOnSubmit = submitRef.current?.childNodes.entries().some(([_index, node]) => {
        return node === event.relatedTarget;
      });
      if (hasBlurredOnSubmit) {
        setPendingSubmit(true);
      }
      executePostEmailAlreadyExist({ email: fieldValues.email });
    },
    [fieldValues.email, executePostEmailAlreadyExist],
  );

  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (data: FieldValues) => {
      if (isValid) {
        const birthDateRaw = data.birthDate
          ? dayjs(data.birthDate, 'DD/MM/YYYY').toISOString()
          : '';
        const formatted = birthDateRaw ? dayjs(birthDateRaw).format('YYYY-MM-DD') : '';
        const departmentName =
          data.department && typeof data.department === 'object'
            ? (data.department as SelectItem).label
            : data.department;
        const teamName =
          data.area && typeof data.area === 'object' ? (data.area as SelectItem).label : data.area;

        executePostEmployee({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          birthDate: formatted || undefined,
          fiscalCode: data.fiscalCode || undefined,
          role: data.role || undefined,
          department: departmentName || undefined,
          area: teamName || undefined,
          emailManager: data.emailManager || undefined,
          jobLevel: data.jobLevel || undefined,
          successSaga: () => {
            notify(tShared('success.genericApiSuccess'), 'success');
            closeDialog();
            executeGetEmployees({});
            executeGetEmployeesStats({});
          },
          errorSaga: response => {
            if (response.error.type === '/problems/duplicate-employee') {
              return notify(t('errors.emailAlreadyInUse'), 'error');
            }
            return notify(tShared('errors.genericApiError'), 'error');
          },
        });
      }
    },
    [
      closeDialog,
      executeGetEmployees,
      executeGetEmployeesStats,
      executePostEmployee,
      isValid,
      notify,
      t,
      tShared,
    ],
  );

  useEffect(() => {
    if (pendingSubmit && !loadingPostEmailAlreadyExist && !emailAlreadyExist) {
      setPendingSubmit(false);
      submitRef.current?.querySelector('button')?.click();
    }
  }, [pendingSubmit, emailAlreadyExist, loadingPostEmailAlreadyExist]);

  useEffect(() => {
    if (isOpen) {
      executeGetDepartments({});
    } else {
      reset();
      setIsDetailsOpen(false);
      setTeamsList([]);
      setEmailChecked('');
      setPendingSubmit(false);
    }
  }, [isOpen, executeGetDepartments, reset]);

  return (
    <Dialog
      name='addEmployeeManual'
      open={isOpen}
      onClose={closeDialog}
      closeButton
      maxWidth='900px'
    >
      <Box display='flex' flexDirection='column' width='100%'>
        <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
        <Typography variant='p2/light' mt='xs' color='primary/text' textAlign='center'>
          {t('subtitle')}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Row 1: First name, Last name, Email */}
          <Box display='flex' mt='m' flexDirection={rowDirection} style={{ gap: gapXS }}>
            <Box flex='1'>
              <Form.FormInput
                // @ts-ignore
                control={control}
                name='firstName'
                label={tEditProfile('fields.firstName')}
                placeholder={tEditProfile('fields.firstName')}
                additonalProps={{ required: true }}
                hasError={!!touchedFields.firstName && fieldValues.firstName === ''}
                labelColor='grey/light'
                data-testid='add-employee-first-name'
              />
            </Box>
            <Box flex='1'>
              <Form.FormInput
                // @ts-ignore
                control={control}
                name='lastName'
                label={tEditProfile('fields.lastName')}
                placeholder={tEditProfile('fields.lastName')}
                additonalProps={{ required: true }}
                hasError={!!touchedFields.lastName && fieldValues.lastName === ''}
                labelColor='grey/light'
                data-testid='add-employee-last-name'
              />
            </Box>
            <Box flex='1' onBlur={handleEmailBlur}>
              <Form.FormInput
                // @ts-ignore
                control={control}
                name='email'
                label={tEditProfile('fields.email')}
                placeholder={tEditProfile('fields.email')}
                additonalProps={{ required: true }}
                hasError={
                  isEmailAlreadyRegistered ||
                  (!!touchedFields.email &&
                    (fieldValues.email === '' || !EMAIL_REGEX.test(fieldValues.email)))
                }
                labelColor='grey/light'
                errorMessage={
                  isEmailAlreadyRegistered ? t('errors.emailAlreadyRegistered') : undefined
                }
                errorColor='primary/text'
                data-testid='add-employee-email'
              />
            </Box>
          </Box>

          {/* Optional details toggle */}
          <Box mt='m' display='flex' flexDirection='row' alignItems='center'>
            <Box
              display='flex'
              flexDirection='row'
              alignItems='center'
              onClick={() => setIsDetailsOpen(prev => !prev)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <Typography variant='p3/light' mt='none' color='white'>
                {t('optionalDetails')}
              </Typography>
              <Box
                ml='xxs'
                display='flex'
                alignItems='center'
                justifyContent='center'
                style={{
                  transition: 'transform 0.2s',
                  transform: isDetailsOpen ? 'rotate(180deg)' : undefined,
                }}
              >
                <Svg name='down' size='s' />
              </Box>
            </Box>
          </Box>

          {isDetailsOpen && (
            <Box mt='xs'>
              <AlertBadge type='primary' iconName='info' label={t('infoAlert')} />

              {/* Role information */}
              <Typography mt='s' variant='p2/bold'>
                {t('roleInfo')}
              </Typography>

              {/* Row 2: Role, Job level */}
              <Box display='flex' mt='xs' flexDirection={rowDirection} style={{ gap: gapXS }}>
                <Box flex='1'>
                  <Form.FormInput
                    // @ts-ignore
                    control={control}
                    name='role'
                    label={tEditProfile('fields.role')}
                    placeholder={tEditProfile('fields.role')}
                    labelColor='grey/light'
                    data-testid='add-employee-role'
                  />
                </Box>
                <Box flex='1'>
                  <Form.FormInput
                    // @ts-ignore
                    control={control}
                    name='jobLevel'
                    label={tEditProfile('fields.jobLevel')}
                    placeholder={tEditProfile('fields.jobLevel')}
                    labelColor='grey/light'
                    data-testid='add-employee-job-level'
                  />
                </Box>
                <Box flex='1' />
              </Box>

              {/* Row 3: Department, Team, Manager */}
              <Box display='flex' mt='xs' flexDirection={rowDirection} style={{ gap: gapXS }}>
                <Box flex='1'>
                  <Box
                    style={{
                      opacity: !loadingGetDepartments && deptItems.length === 0 ? 0.5 : 1,
                      pointerEvents:
                        !loadingGetDepartments && deptItems.length === 0 ? 'none' : 'auto',
                    }}
                  >
                    <Form.FormDropdown
                      // @ts-ignore
                      control={control}
                      name='department'
                      label={tEditProfile('fields.department')}
                      placeholder={tEditProfile('fields.departmentPlaceholder')}
                      items={deptItems}
                      loading={loadingGetDepartments}
                      backgroundColorHeader='grey/light'
                      onItemSelect={handleDeptSelect}
                      labelStyle={{ color: 'grey/light' }}
                      data-testid='add-employee-department'
                    />
                  </Box>
                </Box>
                <Box flex='1'>
                  <Box
                    style={{
                      opacity: !isTeamEnabled ? 0.5 : 1,
                      pointerEvents: !isTeamEnabled ? 'none' : 'auto',
                    }}
                  >
                    <Form.FormDropdown
                      // @ts-ignore
                      control={control}
                      name='area'
                      label={tEditProfile('fields.area')}
                      placeholder={tEditProfile('fields.teamPlaceholder')}
                      items={teamItems}
                      loading={loadingGetDepartmentTeams}
                      backgroundColorHeader='grey/light'
                      onItemSelect={handleTeamSelect}
                      labelStyle={{ color: 'grey/light' }}
                      data-testid='add-employee-area'
                    />
                  </Box>
                </Box>
                <Box flex='1'>
                  <Form.FormInput
                    // @ts-ignore
                    control={control}
                    name='emailManager'
                    label={tEditProfile('fields.manager')}
                    placeholder={tEditProfile('fields.manager')}
                    hasError={
                      fieldValues.emailManager !== '' && !EMAIL_REGEX.test(fieldValues.emailManager)
                    }
                    labelColor='grey/light'
                    data-testid='add-employee-email-manager'
                  />
                </Box>
              </Box>

              {/* Personal details */}
              <Typography mt='s' variant='p2/bold'>
                {t('personalDetails')}
              </Typography>

              {/* Row 4: Birth date, Fiscal code */}
              <Box display='flex' mt='xs' flexDirection={rowDirection} style={{ gap: gapXS }}>
                <Box flex='1'>
                  <Typography
                    variant='p2/light'
                    mb='xxs'
                    fontWeight='400'
                    mt='none'
                    color='grey/light'
                  >
                    {tEditProfile('fields.birthDate')}
                  </Typography>
                  <Form.FormDate
                    // @ts-ignore
                    control={control}
                    name='birthDate'
                    placeholder={tEditProfile('fields.birthDatePlaceholder')}
                    minDate={dayjs().subtract(100, 'year').format('DD/MM/YYYY')}
                    data-testid='add-employee-birth-date'
                  />
                </Box>
                <Box flex='1'>
                  <Form.FormInput
                    // @ts-ignore
                    control={control}
                    name='fiscalCode'
                    label={tEditProfile('fields.fiscalCode')}
                    placeholder={tEditProfile('fields.fiscalCode')}
                    labelColor='grey/light'
                  />
                </Box>
                <Box flex='1' />
              </Box>
            </Box>
          )}

          {/* Action buttons */}
          <Box
            mt='m'
            alignItems='center'
            display='flex'
            flexDirection='row'
            justifyContent='flex-end'
          >
            <Form.Buttons.Cta variant='link/primary' onClick={closeDialog} mr='s' mt='s'>
              {t('buttons.cancel')}
            </Form.Buttons.Cta>
            {/* ref is setted on span because Form.Buttons.Submit don't accept ref attribute */}
            <span ref={submitRef}>
              <Form.Buttons.Submit
                loading={loadingPostEmployee}
                disabled={!canSubmit || loadingPostEmployee || loadingPostEmailAlreadyExist}
                mt='s'
                data-testid='button-save-add-employee'
              >
                {t('buttons.save')}
              </Form.Buttons.Submit>
            </span>
          </Box>
        </form>
      </Box>
    </Dialog>
  );
};
